import React, { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "components/card";
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdClose, MdExpandMore, MdExpandLess,
} from "react-icons/md";
import { getFaqList, createFaq, updateFaq, deleteFaq } from "api/adapters/admin/admin";

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

// ─── FAQ Form Modal ─────────────────────────────────────────────────────────
function FaqModal({ faq, onClose, onSave, saving, error }) {
  const [sQuestion, setQuestion] = useState(faq?.sQuestion || "");
  const [sAnswer, setAnswer] = useState(faq?.sAnswer || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sQuestion.trim() || !sAnswer.trim()) return;
    onSave({ sQuestion: sQuestion.trim(), sAnswer: sAnswer.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-[#111111] shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <h3 className="text-base font-bold text-navy-700 dark:text-white">
            {faq ? "Edit FAQ" : "Add New FAQ"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
            <MdClose className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {error && (
            <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
              {error}
            </p>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Question
            </label>
            <input
              value={sQuestion}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter the FAQ question..."
              required
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white dark:focus:border-orange-400"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Answer
            </label>
            <textarea
              value={sAnswer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter the FAQ answer..."
              required
              rows={4}
              className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white dark:focus:border-orange-400"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 rounded-xl bg-orange-500 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60 transition-colors">
              {saving ? "Saving..." : faq ? "Update FAQ" : "Add FAQ"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ─────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel, loading, error }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-[#111111] p-6 shadow-2xl">
        <p className="mb-6 text-center text-sm font-medium text-navy-700 dark:text-white">{message}</p>
        {error && (
          <p className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-400">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60">
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
const FAQsManagement = () => {
  const queryClient = useQueryClient();
  const [sSearch, setSearch] = useState("");
  const [nPage, setPage] = useState(1);
  const [modal, setModal] = useState(null); // null | { faq?: object }
  const [modalError, setModalError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const debouncedSearch = useCallback(
    debounce((val) => { setSearch(val); setPage(1); }, 400),
    []
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["faqList", sSearch, nPage],
    queryFn: () => getFaqList({ sSearch, nPage, nLimit: 10 }),
    keepPreviousData: true,
  });

  const list = data?.data;
  const faqs = list?.aFaqs ?? [];
  const nTotalPages = list?.nTotalPages ?? 1;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["faqList"] });

  const { mutate: doCreate, isPending: creating } = useMutation({
    mutationFn: createFaq,
    onSuccess: () => { invalidate(); setModal(null); setModalError(""); },
    onError: (err) => setModalError(err.message || "Failed to create FAQ."),
  });

  const { mutate: doUpdate, isPending: updatingFaq } = useMutation({
    mutationFn: ({ id, ...d }) => updateFaq(id, d),
    onSuccess: () => { invalidate(); setModal(null); setModalError(""); },
    onError: (err) => setModalError(err.message || "Failed to update FAQ."),
  });

  const { mutate: doDelete, isPending: deleting } = useMutation({
    mutationFn: () => deleteFaq(deleteId),
    onSuccess: () => { invalidate(); setDeleteId(null); setDeleteError(""); },
    onError: (err) => setDeleteError(err.message || "Failed to delete FAQ."),
  });

  const handleSave = (formData) => {
    setModalError("");
    if (modal?.faq) doUpdate({ id: modal.faq._id, ...formData });
    else doCreate(formData);
  };

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-64">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              onChange={(e) => debouncedSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#111111] dark:text-white"
            />
          </div>
          <button
            onClick={() => setModal({})}
            className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 transition-colors shadow-md shadow-orange-200 dark:shadow-none"
          >
            <MdAdd className="h-5 w-5" />
            Add FAQ
          </button>
        </div>

        {/* FAQ Accordion List */}
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />
            ))}
          </div>
        ) : faqs.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-4xl mb-3">🙋</p>
            <p className="text-sm text-gray-400">No FAQs found. Add your first FAQ.</p>
          </div>
        ) : (
          <div className={`flex flex-col gap-2 ${isFetching ? "opacity-60" : ""}`}>
            {faqs.map((faq, idx) => (
              <div key={faq._id}
                className="rounded-xl border border-gray-100 dark:border-white/10 overflow-hidden">
                <div
                  className="flex cursor-pointer items-center justify-between bg-white px-5 py-4 hover:bg-orange-50/50 transition-colors dark:bg-[#1a1a1a] dark:hover:bg-[#222222]"
                  onClick={() => setExpandedId(expandedId === faq._id ? null : faq._id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-xs font-bold text-orange-600 dark:bg-orange-500/20 dark:text-orange-400">
                      {idx + 1}
                    </span>
                    <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
                      {faq.sQuestion}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-3 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setModal({ faq }); }}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-orange-50 hover:text-orange-500 transition-colors dark:hover:bg-[#2a2a2a]"
                    >
                      <MdEdit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteId(faq._id); }}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors dark:hover:bg-[#2a2a2a]"
                    >
                      <MdDelete className="h-4 w-4" />
                    </button>
                    {expandedId === faq._id
                      ? <MdExpandLess className="h-5 w-5 text-orange-500" />
                      : <MdExpandMore className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>
                {expandedId === faq._id && (
                  <div className="border-t border-gray-100 bg-orange-50/30 px-5 py-4 dark:border-white/5 dark:bg-[#141414]">
                    <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                      {faq.sAnswer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {nTotalPages > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-gray-500">Page {nPage} of {nTotalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={nPage === 1}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]">
                Previous
              </button>
              <button onClick={() => setPage((p) => Math.min(nTotalPages, p + 1))} disabled={nPage === nTotalPages}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]">
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {modal !== null && (
        <FaqModal
          faq={modal.faq}
          onClose={() => { setModal(null); setModalError(""); }}
          onSave={handleSave}
          saving={creating || updatingFaq}
          error={modalError}
        />
      )}
      {deleteId && (
        <ConfirmDialog
          message="Are you sure you want to delete this FAQ? This cannot be undone."
          onConfirm={doDelete}
          onCancel={() => { setDeleteId(null); setDeleteError(""); }}
          loading={deleting}
          error={deleteError}
        />
      )}
    </div>
  );
};

export default FAQsManagement;
